import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Network, FileText, AlertTriangle, Paperclip, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../api/client";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";

const CTRL_STATUS_LABELS = { implemented: "Implementado", in_progress: "En progreso", not_started: "No iniciado", not_applicable: "No aplica" };
const CTRL_STATUS_CLS = {
  implemented: "bg-emerald-100 text-emerald-800 border-emerald-200",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200",
  not_started: "bg-slate-100 text-slate-500",
  not_applicable: "bg-slate-100 text-slate-400",
};

export default function SgsiMap() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [onlyGaps, setOnlyGaps] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/sgsi-map?framework=iso27001");
        setData(data);
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, []);

  const totals = useMemo(() => {
    if (!data) return { controls: 0, gaps: 0, covered: 0 };
    let controls = 0, gaps = 0, covered = 0;
    data.phases.forEach((ph) => ph.controls.forEach((c) => {
      controls++;
      if (c.gap) gaps++;
      if ((c.documents?.length > 0 || c.has_evidence) && c.applicability === "applicable") covered++;
    }));
    return { controls, gaps, covered };
  }, [data]);

  if (loading) return <div className="text-slate-500">Cargando el mapa del SGSI...</div>;
  if (!data) return <div className="text-slate-500">No se pudo cargar el mapa del SGSI.</div>;

  const phases = data.phases.filter((ph) => phaseFilter === "all" || ph.code === phaseFilter);

  return (
    <div data-testid="sgsi-map-page" className="max-w-6xl">
      <div className="flex items-center gap-2">
        <Network className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-[20px] font-semibold tracking-tight text-slate-900">Mapa del SGSI</h1>
          <p className="text-[14px] text-slate-500 mt-0.5">Cobertura de controles y documentos por fase. Detecta huecos de cumplimiento.</p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-3 max-w-xl">
        <Kpi label="Controles" value={totals.controls} />
        <Kpi label="Con cobertura" value={totals.covered} tone="emerald" />
        <Kpi label="Huecos" value={totals.gaps} tone="amber" />
      </div>

      <div className="mt-5 flex items-center gap-4 flex-wrap">
        <Select value={phaseFilter} onValueChange={setPhaseFilter}>
          <SelectTrigger className="w-56" data-testid="map-phase-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las fases</SelectItem>
            {data.phases.map((ph) => <SelectItem key={ph.code} value={ph.code}>{ph.code} · {ph.title}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer">
          <Checkbox checked={onlyGaps} onCheckedChange={(v) => setOnlyGaps(!!v)} data-testid="map-only-gaps" />
          Solo huecos
        </label>
      </div>

      <div className="mt-5 space-y-5">
        {phases.map((ph) => {
          const controls = onlyGaps ? ph.controls.filter((c) => c.gap) : ph.controls;
          const steps = onlyGaps ? ph.steps.filter((s) => s.documents.length === 0) : ph.steps;
          if (onlyGaps && controls.length === 0 && steps.length === 0) return null;
          return (
            <div key={ph.code} className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={`map-phase-${ph.code}`}>
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/60">
                <div className="text-[14px] font-semibold text-slate-900">{ph.code} · {ph.title}</div>
              </div>

              {steps.length > 0 && (
                <div className="px-5 py-3 border-b border-slate-100">
                  <div className="text-[12px] uppercase tracking-wide text-slate-400 mb-2">Pasos y documentos</div>
                  <div className="space-y-1.5">
                    {steps.map((s) => (
                      <div key={s.code} className="flex items-center gap-2 flex-wrap" data-testid={`map-step-${s.code}`}>
                        <button onClick={() => navigate(`/ruta/pasos/${s.code}`)} className="text-[13px] text-slate-700 hover:text-primary font-medium">{s.code} · {s.title}</button>
                        {s.documents.length === 0 ? (
                          <span className="text-[11px] text-slate-400">sin documentos</span>
                        ) : s.documents.map((d) => (
                          <span key={d.id} onClick={() => navigate(`/documents/${d.id}`)} className="text-[11px] inline-flex items-center gap-1 border border-slate-200 rounded px-1.5 py-0.5 text-slate-600 cursor-pointer hover:bg-slate-50">
                            <FileText className="h-3 w-3" /> {d.title}
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {controls.length > 0 && (
                <div className="px-5 py-3">
                  <div className="text-[12px] uppercase tracking-wide text-slate-400 mb-2">Controles ({controls.length})</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {controls.map((c) => (
                      <div key={c.code} className={`flex items-center gap-2 border rounded-md px-3 py-2 ${c.gap ? "border-amber-200 bg-amber-50/40" : "border-slate-200"}`} data-testid={`map-control-${c.code}`}>
                        <span className="text-[12px] font-mono text-slate-500 w-14 shrink-0">{c.code}</span>
                        <Badge className={CTRL_STATUS_CLS[c.status] || CTRL_STATUS_CLS.not_started}>{CTRL_STATUS_LABELS[c.status] || c.status}</Badge>
                        <div className="flex-1" />
                        {c.documents?.length > 0 && <span className="text-[11px] text-slate-500 inline-flex items-center gap-0.5" title="Documentos"><FileText className="h-3 w-3" />{c.documents.length}</span>}
                        {c.has_evidence && <Paperclip className="h-3.5 w-3.5 text-emerald-500" title="Con evidencia" />}
                        {c.gap ? (
                          <span className="text-[11px] text-amber-700 inline-flex items-center gap-1 font-medium" data-testid={`gap-${c.code}`}><AlertTriangle className="h-3.5 w-3.5" /> Hueco</span>
                        ) : (c.applicability === "applicable" && (c.documents?.length > 0 || c.has_evidence)) ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-500" title="Cubierto" />
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const cls = tone === "amber" ? "text-amber-700" : tone === "emerald" ? "text-emerald-700" : "text-slate-900";
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-white">
      <div className="text-[12px] text-slate-500">{label}</div>
      <div className={`text-[22px] font-semibold mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}
